import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useThemeStore } from "../../store/theme";
import { Loader2, Sparkles } from "lucide-react";
import {
  getSuggestedUsers,
  searchUsers,
  followUser,
  unfollowUser,
  getCurrentUserFollowing,
  User,
  getLocalData,
} from "../../api";
import { queryLLM } from "../../api/ai.api";
import { WWW_BASE } from "@/api/config";

const RightSidebar: React.FC = () => {
  const navigate = useNavigate();
  const { colors, theme } = useThemeStore();
  const [suggestedAccounts, setSuggestedAccounts] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [followStates, setFollowStates] = useState<Record<string, boolean>>({});
  const [followLoading, setFollowLoading] = useState<Record<string, boolean>>(
    {},
  );
  const [followMessage, setFollowMessage] = useState<string | null>(null);
  const [isAiSuggestions, setIsAiSuggestions] = useState(false);

  // Fetch suggested users and current user's following list on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const thisUser = await getLocalData("idearify-app-storage");
        const currentUser = thisUser?.state?.user;
        setUser(currentUser || null);
        setIsLoadingUsers(true);
        setUsersError(null);
        setIsAiSuggestions(false);

        // Fetch community users and following list in parallel
        const [communityResponse, followingList] = await Promise.all([
          searchUsers({ query: "", limit: 20 }),
          getCurrentUserFollowing(),
        ]);

        let communityList = communityResponse.users.filter(u => u.id !== currentUser?.id);

        if (currentUser && currentUser.bio && communityList.length > 0) {
          try {
            const prompt = `Based on my profile bio: "${currentUser.bio}", please select the 5 most relevant users for me to follow from this list: ${JSON.stringify(communityList.map(u => ({ id: u.id, username: u.username, bio: u.bio })))}. Return ONLY a comma-separated list of their exact user IDs and nothing else.`;
            const aiResponse = await queryLLM({ query: prompt, userId: currentUser.id });

            if (aiResponse && aiResponse.data) {
              const suggestedIds = aiResponse.data.split(',').map((id: string) => id.trim());
              const aiFiltered = communityList.filter(u => suggestedIds.includes(u.id));

              if (aiFiltered.length > 0) {
                communityList = aiFiltered;
                setIsAiSuggestions(true);
              }
            }
          } catch (err) {
            console.error("AI suggestion failed, falling back to recent community users", err);
          }
        }

        // Fallback to limit 5 if AI didn't trim it down
        const finalUsers = communityList.slice(0, 5);
        setSuggestedAccounts(finalUsers);

        // Initialize follow states based on current user's following list
        const initialFollowStates: Record<string, boolean> = {};
        finalUsers.forEach((u) => {
          initialFollowStates[u.id] = followingList.includes(u.id);
        });
        setFollowStates(initialFollowStates);
      } catch (error) {
        console.error("Failed to fetch data:", error);
        setUsersError("Failed to load suggested users");
        // Fallback to empty array
        setSuggestedAccounts([]);
      } finally {
        setIsLoadingUsers(false);
      }
    };

    fetchData();
  }, []);

  // Handle follow/unfollow user
  const handleFollowToggle = async (
    userId: string,
    isCurrentlyFollowing: boolean,
  ) => {
    try {
      setFollowLoading((prev) => ({ ...prev, [userId]: true }));

      if (isCurrentlyFollowing) {
        const result = await unfollowUser(userId, user?.username || "");
        if (result.success) {
          setFollowStates((prev) => ({ ...prev, [userId]: false }));
          setFollowMessage("Successfully unfollowed user");
          setTimeout(() => setFollowMessage(null), 3000);
        }
      } else {
        const result = await followUser(userId, user?.username || "");
        if (result.success) {
          setFollowStates((prev) => ({ ...prev, [userId]: true }));
          setFollowMessage("Successfully followed user");
          setTimeout(() => setFollowMessage(null), 3000);
        }
      }
    } catch (error) {
      console.error("Failed to toggle follow state:", error);
      setFollowMessage("Failed to update follow status");
      setTimeout(() => setFollowMessage(null), 3000);
    } finally {
      setFollowLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  return (
    <aside
      className="w-80 border-l pt-4 pb-6 px-6 fixed right-0 top-16 h-full overflow-y-auto"
      style={{
        backgroundColor: colors.background,
        borderColor: theme === "light" ? "#e5e7eb" : "#374151",
      }}
    >
      {/* Suggested Accounts */}
      <div className="mb-8">
        {followMessage && (
          <div
            className="mb-4 p-3 rounded-lg text-sm text-center transition-opacity"
            style={{
              backgroundColor: followMessage.includes("Successfully")
                ? "#10B981"
                : "#EF4444",
              color: "white",
            }}
          >
            {followMessage}
          </div>
        )}
        <h3
          className="text-lg font-bold mb-4 flex items-center gap-2"
          style={{ color: colors.text }}
        >
          Suggested Creators
          {isAiSuggestions && <Sparkles size={16} style={{ color: colors.primary }} />}
        </h3>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                setIsLoadingUsers(true);
                setUsersError(null);
                Promise.all([
                  getSuggestedUsers({ limit: 5, excludeCurrentUser: true }),
                  getCurrentUserFollowing(),
                ])
                  .then(([suggestedResponse, followingList]) => {
                    setSuggestedAccounts(suggestedResponse.users);
                    const initialFollowStates: Record<string, boolean> = {};
                    suggestedResponse.users.forEach((user) => {
                      initialFollowStates[user.id] = followingList.includes(
                        user.id,
                      );
                    });
                    setFollowStates(initialFollowStates);
                  })
                  .catch((error) => {
                    console.error("Failed to refresh suggested users:", error);
                    setUsersError("Failed to refresh users");
                  })
                  .finally(() => setIsLoadingUsers(false));
              }}
              disabled={isLoadingUsers}
              className="text-sm font-medium hover:opacity-80 transition-opacity disabled:opacity-50 flex items-center space-x-1"
              style={{ color: colors.primary }}
            >
              {isLoadingUsers ? (
                <>
                  <Loader2 className="animate-spin" size={14} />
                  <span>Refreshing...</span>
                </>
              ) : (
                "Refresh"
              )}
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {isLoadingUsers ? (
            <div className="flex items-center justify-center py-4">
              <Loader2
                className="animate-spin"
                size={20}
                style={{ color: colors.gray }}
              />
              <span className="ml-2 text-sm" style={{ color: colors.gray }}>
                Loading users...
              </span>
            </div>
          ) : usersError ? (
            <div className="text-center py-4">
              <p className="text-sm text-red-500 mb-2">{usersError}</p>
              <button
                onClick={() => window.location.reload()}
                className="text-sm font-medium hover:opacity-80 transition-opacity"
                style={{ color: colors.primary }}
              >
                Retry
              </button>
            </div>
          ) : suggestedAccounts.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-sm" style={{ color: colors.gray }}>
                No suggested users found
              </p>
            </div>
          ) : (
            suggestedAccounts.map((account) => (
              <div key={account.id} className="flex items-center space-x-3">
                <div
                  className="flex flex-1 min-w-0 cursor-pointer items-center space-x-3"
                  onClick={() => navigate(`/profile/${account.username}`)}
                >
                  <img
                    src={(!account.avatar || account.avatar === "undefined") ? "/assets/avatar.png" : account.avatar}
                    alt={
                      account.displayName ||
                      account.username
                    }
                    className="w-10 h-10 rounded-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "/assets/avatar.png";
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-1">
                      <span
                        className="text-sm font-medium truncate"
                        style={{ color: colors.text }}
                      >
                        {account.displayName ||
                          `@${account.username}`}
                      </span>
                      {account.verified && (
                        <div
                          className="w-4 h-4 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: colors.primary }}
                        >
                          <span className="text-white text-xs">✓</span>
                        </div>
                      )}
                    </div>
                    <p
                      className="text-xs truncate"
                      style={{ color: colors.gray }}
                    >
                      {account.linkedIn}
                    </p>
                    <p
                      className="text-xs"
                      style={{ color: theme === "light" ? "#9ca3af" : "#6b7280" }}
                    >
                      {account.subscribers || account.followers || 0} followers
                    </p>
                  </div>
                </div>
                <button
                  onClick={() =>
                    handleFollowToggle(
                      account.id,
                      followStates[account.id] || false,
                    )
                  }
                  disabled={followLoading[account.id]}
                  className="text-sm font-medium hover:opacity-80 transition-opacity disabled:opacity-50 min-w-[60px]"
                  style={{
                    color: followStates[account.id]
                      ? colors.gray
                      : colors.primary,
                  }}
                >
                  {followLoading[account.id] ? (
                    <Loader2 className="animate-spin" size={14} />
                  ) : followStates[account.id] ? (
                    "Following"
                  ) : (
                    "Follow"
                  )}
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Footer Links */}
      <div
        className="pt-6 border-t"
        style={{ borderColor: theme === "light" ? "#e5e7eb" : "#374151" }}
      >
        <div className="flex flex-wrap gap-2 text-xs">
          <a
            href={`${WWW_BASE}/company`}
            className="hover:opacity-80 transition-opacity"
            style={{ color: colors.gray }}
          >
            About
          </a>
          <span style={{ color: colors.gray }}>•</span>
          {/* <a
                        href="#"
                        className="hover:opacity-80 transition-opacity"
                        style={{ color: colors.gray }}
                    >
                        Contact
                    </a> */}
        </div>

        {/* <div className="flex flex-wrap gap-2 text-xs mt-2">
                    <a
                        href="#"
                        className="hover:opacity-80 transition-opacity"
                        style={{ color: colors.gray }}
                    >
                        Advertise
                    </a>
                </div> */}

        <div className="flex flex-wrap gap-2 text-xs mt-2">
          <a
            href={`${WWW_BASE}/legal#terms`}
            className="hover:opacity-80 transition-opacity"
            style={{ color: colors.gray }}
          >
            Terms
          </a>
          <span style={{ color: colors.gray }}>•</span>
          <a
            href={`${WWW_BASE}/legal#privacy-policy`}
            className="hover:opacity-80 transition-opacity"
            style={{ color: colors.gray }}
          >
            Privacy
          </a>
        </div>

        <div className="flex flex-wrap gap-2 text-xs mt-2">
          <a
            href={`${WWW_BASE}/legal#community-guidelines`}
            className="hover:opacity-80 transition-opacity"
            style={{ color: colors.gray }}
          >
            Community Guidelines
          </a>
        </div>

        <p
          className="text-xs mt-4"
          style={{ color: theme === "light" ? "#9ca3af" : "#6b7280" }}
        >
          Idearify © 2026. All rights reserved.
        </p>
      </div>
    </aside>
  );
};

export default RightSidebar;
